/**
 * The replay port (D163, platform.md §6): a session on one version of a
 * game, carried onto a newer one — exactly when it can be, and honestly
 * when it cannot.
 *
 * @remarks
 * A session IS `(bundle, seed, commands)` (D09), so moving it to a new
 * bundle is a replay: the same seed and the same commands against the new
 * pack, from genesis. Every command that lands as it did is kept; the first
 * that does not is the report. "Lands as it did" is judged on the player's
 * FOOTPRINT — the verdict on their words, the room they end in, what they
 * carry, whether the game is asking them something, and which endings have
 * been selected — and never on prose or on the world's private state. A
 * rewrite ports in silence; a re-routed exit, a locker that stops opening,
 * a key that is no longer where it was, do not. Comparing whole states
 * would interrupt every live session for a flag rename no player could
 * perceive; comparing verdicts alone would move a player somewhere else
 * without a word. The footprint is exactly what the report can say in the
 * player's own terms, so the report is the diff, not a guess.
 *
 * A partial port lands on the new version's EXACT state after the last
 * matching command — genesis plus the kept commands, byte-exact by the
 * determinism contract — never on a checkpoint restore: the last matching
 * command is always at or past the last checkpoint, and it is the truth
 * rather than the world-not-player approximation death needs (D142).
 *
 * Pure: two replays, no clock, no I/O. Its cost is the session's length
 * twice over, paid once per publication per session, on wake.
 *
 * **Where the commands come from (Calder's note, D164).** The platform holds
 * them as the session's record (D09); the page holds them in the SAVE —
 * `RuntimeState.session.commandHistory`, every input verbatim, kept since
 * R10 for the arrow-key history and the question's ordinal, never for
 * this. The port in the page was cheap because a field added for another
 * reason was never removed. The lesson is not "keep everything"; it is
 * that a state which records what the player DID, rather than what it
 * produced, is replayable by construction — which is D09's whole claim,
 * and here it paid.
 *
 * @packageDocumentation
 */

import type { PortDifference, PortReport, TurnVerdict } from '@orpheus/api-contracts';
import type { LoadedGameBundle } from '@orpheus/content-loader';
import type { RecordLayer, RuntimeState } from '@orpheus/content-schema';

import { type PlayerInputOutcome, processPlayerInput } from '@orpheus/engine-runtime';

import { genesisOf, nextStateOf } from './derive-session-view.js';

/** Where the player stands, as they could notice it: the room, what they carry, whether they are asked, which endings stand. */
export interface Standing {
	readonly carriedItemIds: readonly string[];
	readonly isAsked: boolean;
	readonly roomId: string;
	readonly selectedEndingIds: readonly string[];
}

/** What the player could notice about one turn — where it left them, and the verdict on their words: the whole of what the port compares. */
export interface TurnFootprint extends Standing {
	readonly verdict: TurnVerdict;
}

/** One command replayed: the state after it, and its footprint. */
interface ReplayedTurn {
	readonly footprint: TurnFootprint;
	readonly state: RuntimeState;
}

/** The outcome of a port: whole, or as far as it goes with the report of where it stopped. */
export type PortOutcome =
	| { readonly kind: 'full'; readonly state: RuntimeState }
	| { readonly kind: 'partial'; readonly report: PortReport; readonly state: RuntimeState };

/**
 * D250: the outcome of a whole move — onto the new version with every command, or refused with what would differ
 * where the player ends up.
 */
export type MoveOutcome =
	| { readonly commands: readonly string[]; readonly kind: 'moved'; readonly state: RuntimeState }
	| { readonly differences: readonly PortDifference[]; readonly kind: 'refused'; }
	| { readonly kind: 'unmatched'; readonly position: number };

/**
 * D262: one command the operator changes on the way — named by its position (from 1) and the words typed there, so an
 * edit aimed at the wrong turn is refused rather than applied; replaced by other words, or dropped (`null`).
 */
export interface CommandEdit {
	readonly now: string | null;
	readonly position: number;
	readonly was: string;
}

/** What the port needs: both versions, the seed, the commands, and the new version's hash for the report. */
export interface PortSessionOptions {
	readonly commands: readonly string[];
	readonly from: LoadedGameBundle;
	/** D241: the Record the session began on — an input like the seed, so both replays begin on it. Absent, none. */
	readonly record?: readonly RecordLayer[];
	readonly seed: number;
	readonly to: LoadedGameBundle;
	readonly toBundleHash: string;
}

const sameList = (left: readonly string[], right: readonly string[]): boolean =>
	left.length === right.length && left.every((entry, index) => entry === right[index]);

/** Where a state leaves the player, as they could notice it. */
const standingOf = (state: RuntimeState): Standing => ({
	// Sorted: inventory ORDER is the world's business, what is carried is the player's.
	carriedItemIds: [...state.world.inventoryItemIds].sort(),
	isAsked: state.session.pendingQuestion !== undefined,
	roomId: state.progression.activeRoomId,
	selectedEndingIds: state.narrative.selectedEndingIds,
});

/**
 * A session, replayed: its commands against a bundle from genesis on its seed and Record — the one replay the
 * port, the move and the session object's hot state all are (D09).
 *
 * @param bundle - The version to replay on
 * @param session - The seed, the Record it began on, and the commands
 * @returns The state after the last command
 */
export const replaySession = (bundle: LoadedGameBundle, session: Pick<PortSessionOptions, 'commands' | 'record' | 'seed'>): RuntimeState =>
	session.commands.reduce<RuntimeState>((state, command) => nextStateOf(processPlayerInput(bundle, state, command)), genesisOf(bundle, session.seed, session.record));

/**
 * The footprint one turn left: where it left the player, and the verdict on their words — what a live turn records
 * (D257) and what a replayed one is compared on.
 *
 * @param outcome - The turn's outcome
 * @returns Its footprint
 */
export const footprintOf = (outcome: PlayerInputOutcome): TurnFootprint => ({ ...standingOf(nextStateOf(outcome)), verdict: outcome.type });

/** Run one command and photograph the player's footprint after it — the port's step, and D257's trace's source. */
export const replayOne = (bundle: LoadedGameBundle, state: RuntimeState, command: string): ReplayedTurn => {
	const outcome = processPlayerInput(bundle, state, command);

	return { footprint: footprintOf(outcome), state: nextStateOf(outcome) };
};

/**
 * Every way two standings differ, named for the report; empty when the player stands where they did.
 *
 * @param was - Where they stand on the version they played
 * @param now - Where they would stand on the other
 * @returns The differences, in the order the report lists them
 */
const standingDifferences = (was: Standing, now: Standing): readonly PortDifference[] => {
	const differences: PortDifference[] = [];

	if (was.roomId !== now.roomId) {
		differences.push('room');
	}

	if (!sameList(was.carriedItemIds, now.carriedItemIds)) {
		differences.push('carried');
	}

	if (was.isAsked !== now.isAsked) {
		differences.push('question');
	}

	if (!sameList(was.selectedEndingIds, now.selectedEndingIds)) {
		differences.push('ending');
	}

	return differences;
};

/**
 * Every way two footprints differ, named for the report; empty when the
 * turn landed as it did.
 *
 * @param was - The footprint on the version the player played
 * @param now - The footprint on the version they would move to
 * @returns The differences, in the order the report lists them
 */
export const footprintDifferences = (was: TurnFootprint, now: TurnFootprint): readonly PortDifference[] => [
	...(was.verdict === now.verdict ? [] : (['verdict'] as const)),
	...standingDifferences(was, now),
];

/**
 * Replay a session's commands against a newer bundle, as far as they land
 * the same.
 *
 * @param options - Both versions, the seed, the commands, and the new hash
 * @returns The state on the new version, whole or partial with the report
 */
export const portSession = (options: PortSessionOptions): PortOutcome => {
	const { commands, from, record, seed, to, toBundleHash } = options;
	let was = genesisOf(from, seed, record);
	let now = genesisOf(to, seed, record);

	for (const [index, command] of commands.entries()) {
		const before = replayOne(from, was, command);
		const after = replayOne(to, now, command);
		const differences = footprintDifferences(before.footprint, after.footprint);

		if (differences.length > 0) {
			return {
				kind: 'partial',
				report: {
					divergedAt: { command, differences, now: after.footprint.verdict, position: index + 1, was: before.footprint.verdict },
					keptCommands: index,
					toBundleHash,
					totalCommands: commands.length,
				},
				state: now,
			};
		}

		was = before.state;
		now = after.state;
	}

	return { kind: 'full', state: now };
};

/**
 * The whole move (D250): every command, replayed on the new version, kept —
 * when the player ends up standing where they stand now.
 *
 * @remarks
 * The port judges each turn, because a player who is MOVED must not find
 * themselves somewhere else without a word, and the first turn that lands
 * differently is where it stops. That is right for a move nobody chose, and
 * too strict for one an operator chooses on a player's behalf: a fix to a
 * word they typed (Mike's `use band`, refused and then answered) parts the
 * versions at that turn and can bring them back together by the last. This
 * move judges the END instead — the same standing the port compares, less
 * the verdict on the last command — so it refuses exactly what a player
 * could notice, and nothing they could not.
 *
 * **The operator's edits (D262).** Sometimes the versions do not come back
 * together: a search the old version answered is refused by the new one, and
 * a refused turn passes no time, so every clock after it is a turn out (Mike's
 * of 2026-11-02, 118 commands in). The move then takes edits — a command
 * swapped for words that land as the old ones did, or one the old version
 * refused (and so did nothing) dropped — applied to the new version's replay
 * only. The judgment does not change: the OLD commands on the old version
 * against the EDITED ones on the new, at the end. An edit is a proposal the
 * standing check accepts or refuses, never a way round it.
 *
 * @param options - Both versions, the seed, the Record and the commands
 * @param edits - The operator's changes to the commands, each checked against the words at its position
 * @returns The new version's state and the commands that reach it, the differences that refuse it, or the first edit
 * that does not match what was typed
 */
export const moveSession = (options: Omit<PortSessionOptions, 'toBundleHash'>, edits: readonly CommandEdit[] = []): MoveOutcome => {
	const unmatched = edits.find(({ position, was }, index) =>
		options.commands[position - 1] !== was || edits.findIndex((edit) => edit.position === position) !== index);

	if (unmatched !== undefined) {
		return { kind: 'unmatched', position: unmatched.position };
	}

	const commands = options.commands.flatMap((command, index) => {
		const edit = edits.find(({ position }) => position === index + 1);

		return edit === undefined ? [command] : edit.now === null ? [] : [edit.now];
	});
	const now = replaySession(options.to, { ...options, commands });
	const differences = standingDifferences(standingOf(replaySession(options.from, options)), standingOf(now));

	return differences.length === 0 ? { commands, kind: 'moved', state: now } : { differences, kind: 'refused' };
};
